import React from 'react';

const KW = {
  carteGrise: ['carte grise', 'immatriculation', 'taxe siv', "certificat d'immatriculation"],
  maintenance: ['maintenance', 'entretien', 'révision', 'revision', 'contrat entretien'],
  assistance: ['assistance', 'dépannage', 'depannage', 'garantie panne', 'panne mécanique'],
  remplacement: ['remplacement', 'véhicule de prêt', 'vehicule de pret', 'véhicule de remplacement', 'courtoisie', 'prêt véhicule'],
};

function stripAccents(s) {
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Ne pas exposer dans le récap les lignes liées à la perte financière / DIT / GPF. */
function textMentionsFinancialLoss(text) {
  if (!text || typeof text !== 'string') return false;
  const t = stripAccents(text);
  if (t.includes('perte financiere') || t.includes('perte financi')) return true;
  if (t.includes('gpf')) return true;
  if (t.includes('garantie emprunteur')) return true;
  if (t.includes('deces incapacite') || t.includes('incapacite travail')) return true;
  if (t.includes('valeur a neuf')) return true;
  if (/\bdit\b/.test(t)) return true;
  if (/\bd\.i\.t\.?\b/.test(t)) return true;
  return false;
}

function formatDigestMatch(formatCurrency, digest, keywordList) {
  if (!digest || typeof digest !== 'object') return null;
  const candidates = [];
  const consider = (text, amt) => {
    if (!text || typeof text !== 'string') return;
    if (textMentionsFinancialLoss(text)) return;
    const t = stripAccents(text);
    const normalizedKeywords = keywordList.map((k) => stripAccents(k));
    if (normalizedKeywords.some((k) => t.includes(k))) {
      candidates.push({ text: text.trim(), amt });
    }
  };
  for (const it of digest.insurance_items || []) {
    if (!it || typeof it !== 'object') continue;
    consider(`${it.title || ''} ${it.note || ''}`, it.monthly_amount_ttc);
  }
  for (const line of digest.location_extra_lines || []) {
    if (typeof line === 'string') consider(line, null);
  }
  for (const it of digest.annex_contracts || []) {
    if (!it || typeof it !== 'object') continue;
    consider(`${it.title || ''} ${it.description || ''}`, it.monthly_amount_ttc);
  }
  if (candidates.length === 0) return null;
  const c = candidates[0];
  if (c.amt != null && !Number.isNaN(Number(c.amt))) {
    return `${formatCurrency(Number(c.amt))} / mois`;
  }
  const snippet = c.text.replace(/^[-*•\s]+/, '').slice(0, 90);
  return snippet || 'Prévu au contrat';
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-neutral-100 py-3 text-sm last:border-0">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span className="font-medium text-neutral-900 text-right break-words">{value}</span>
    </div>
  );
}

/**
 * Affichage réduit après scan : champs principaux du contrat + services recherchés dans le digest JSON.
 */
export default function PostScanSummary({ result, formatCurrency }) {
  const digest = result?.digest && typeof result.digest === 'object' ? result.digest : null;
  const cd = result?.contract_details || {};
  const sa = result?.savings_analysis || {};
  const v = result?.vehicle || {};

  const duration = digest?.duration_months ?? cd.duration_months;
  const deposit = digest?.deposit_ttc;
  const km = digest?.contract_mileage_km ?? cd.total_mileage;

  let loyer = sa.current_monthly;
  if (loyer == null && digest) {
    loyer = digest.subsequent_loyers_ttc ?? digest.monthly_payment_ttc ?? digest.first_loyer_ttc;
  }
  if (loyer == null) loyer = cd.monthly_payment;

  const fmtDur =
    duration != null && duration !== '' && !Number.isNaN(Number(duration)) ? `${Math.round(Number(duration))} mois` : '—';
  const fmtDep =
    deposit != null && deposit !== '' && !Number.isNaN(Number(deposit)) ? formatCurrency(Number(deposit)) : '—';
  const fmtKm =
    km != null && km !== '' && !Number.isNaN(Number(km)) ? `${Number(km).toLocaleString('fr-FR')} km` : '—';
  const fmtLoyer =
    loyer != null && loyer !== '' && !Number.isNaN(Number(loyer)) ? formatCurrency(Number(loyer)) : '—';

  const carteGrise = digest ? formatDigestMatch(formatCurrency, digest, KW.carteGrise) : null;
  const maintenance = digest ? formatDigestMatch(formatCurrency, digest, KW.maintenance) : null;
  const assistance = digest ? formatDigestMatch(formatCurrency, digest, KW.assistance) : null;
  const remplacement = digest ? formatDigestMatch(formatCurrency, digest, KW.remplacement) : null;

  const sub =
    v.make || v.model
      ? [v.make, v.model, v.year].filter(Boolean).join(' ')
      : result?.document_type || 'Contrat';

  return (
    <div className="bg-white border border-neutral-200 p-6 md:p-8" data-testid="post-scan-summary">
      <h2 className="text-xl font-bold tracking-tight text-neutral-950 font-heading">
        Synthèse du contrat
      </h2>
      <p className="text-sm text-neutral-500 mt-1 mb-6">{sub}</p>
      <div>
        <SummaryRow label="Durée" value={fmtDur} />
        <SummaryRow label="Dépôt de garantie" value={fmtDep} />
        <SummaryRow label="Kilométrage contractuel" value={fmtKm} />
        <SummaryRow label="Loyer" value={fmtLoyer} />
        <SummaryRow label="Carte grise" value={carteGrise || '—'} />
        <SummaryRow label="Maintenance" value={maintenance || '—'} />
        <SummaryRow label="Assistance constructeur" value={assistance || '—'} />
        <SummaryRow label="Véhicule de remplacement" value={remplacement || '—'} />
      </div>
    </div>
  );
}
